/**
 * When this account is allowed to telephone people.
 *
 * A grid of seven days, each with any number of open windows. "Monday to Saturday,
 * 9 to 6, Sunday off" is the common case and takes two clicks via the presets; the
 * per-day rows exist because a lunch break ("09:00–13:00, 14:00–18:00") is a real
 * request and modelling it as one window with a hole in it would be a lie.
 *
 * A day with no window is closed. That is stated on screen rather than implied,
 * because an empty row looks like a form someone forgot to fill in — and the
 * consequence (nothing is ever dialled that day) is invisible otherwise.
 */

import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SettingsSection } from "@/components/ui/settings-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { errorMessage, type AccountConfig, type CallingWindow } from "@/lib/api";
import { useSaveWindows } from "@/lib/queries";
import { weekdayName } from "@/lib/labels";

const DAYS = [1, 2, 3, 4, 5, 6, 7];

const PRESETS: { label: string; windows: CallingWindow[] }[] = [
  {
    label: "Mon–Sat, 09:00–18:00",
    windows: [1, 2, 3, 4, 5, 6].map((weekday) => ({ weekday, start: "09:00", end: "18:00" })),
  },
  {
    label: "Mon–Fri, 09:00–18:00",
    windows: [1, 2, 3, 4, 5].map((weekday) => ({ weekday, start: "09:00", end: "18:00" })),
  },
  {
    label: "Every day, 10:00–19:00",
    windows: DAYS.map((weekday) => ({ weekday, start: "10:00", end: "19:00" })),
  },
];

export function CallingHoursSection({
  accountId,
  config,
}: {
  accountId: string;
  config: AccountConfig;
}) {
  const save = useSaveWindows(accountId);
  const [windows, setWindows] = useState<CallingWindow[]>(config.calling_windows);
  const [dirty, setDirty] = useState(false);

  // Adopt the server's copy whenever it changes underneath us — after a save, or
  // if someone else edited the same account.
  useEffect(() => {
    if (!dirty) setWindows(config.calling_windows);
  }, [config.calling_windows, dirty]);

  const update = (next: CallingWindow[]) => {
    setWindows(next);
    setDirty(true);
  };

  const forDay = (weekday: number) =>
    windows
      .map((w, index) => ({ w, index }))
      .filter(({ w }) => w.weekday === weekday)
      .sort((a, b) => a.w.start.localeCompare(b.w.start));

  const invalid = windows.some((w) => w.end <= w.start);

  return (
    <SettingsSection
      title="Calling hours"
      description={`All times are ${config.account.timezone}. A callback asked for outside these hours is moved to the next open window, and every retry obeys them too.`}
      footer={
        <>
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => {
              setWindows(config.calling_windows);
              setDirty(false);
            }}
          >
            Discard
          </Button>
          <Button
            size="sm"
            className="min-w-[120px]"
            disabled={!dirty || invalid || save.isPending}
            onClick={() =>
              save.mutate(windows, {
                onSuccess: (r) => {
                  setDirty(false);
                  toast.success(r.message);
                },
                onError: (e) => toast.error(errorMessage(e)),
              })
            }
          >
            {save.isPending ? "Saving…" : "Save hours"}
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2 pb-1">
        <span className="text-xs text-muted-foreground">Start from:</span>
        {PRESETS.map((p) => (
          <Button
            key={p.label}
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => update(p.windows)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <div className="divide-y divide-border rounded-lg border border-border">
        {DAYS.map((day) => {
          const rows = forDay(day);
          return (
            <div key={day} className="flex items-start gap-4 px-4 py-3">
              <div className="w-24 shrink-0 pt-1.5 text-sm font-medium">{weekdayName(day)}</div>

              <div className="min-w-0 flex-1 space-y-2">
                {rows.length === 0 ? (
                  <p className="pt-1.5 text-sm text-muted-foreground">
                    Closed — no calls go out on this day.
                  </p>
                ) : (
                  rows.map(({ w, index }) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={w.start}
                        aria-label={`${weekdayName(day)} start`}
                        className="h-8 w-[110px]"
                        onChange={(e) =>
                          update(
                            windows.map((x, i) =>
                              i === index ? { ...x, start: e.target.value } : x,
                            ),
                          )
                        }
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="time"
                        value={w.end}
                        aria-label={`${weekdayName(day)} end`}
                        className="h-8 w-[110px]"
                        aria-invalid={w.end <= w.start}
                        onChange={(e) =>
                          update(
                            windows.map((x, i) => (i === index ? { ...x, end: e.target.value } : x)),
                          )
                        }
                      />
                      {w.end <= w.start && (
                        <span className="text-xs text-destructive">must end after it starts</span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label={`Remove ${weekdayName(day)} window`}
                        onClick={() => update(windows.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 text-xs"
                onClick={() =>
                  update([
                    ...windows,
                    {
                      weekday: day,
                      // Continue after the last window rather than colliding with it.
                      start: rows.length ? rows[rows.length - 1]!.w.end : "09:00",
                      end: rows.length ? "21:00" : "18:00",
                    },
                  ])
                }
              >
                <Plus className="size-3.5" />
                Add window
              </Button>
            </div>
          );
        })}
      </div>

      {windows.length === 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            With no open windows nothing will ever be dialled for this account, however full the
            queue gets.
          </AlertDescription>
        </Alert>
      )}
    </SettingsSection>
  );
}
